import React from 'react';
import moment from 'moment';
import { defineMessages, useIntl } from 'react-intl';
import { InfiniteScroll } from '@lifeomic/chroma-react/components/InfiniteScroll';
import {
  TableModule,
  TableConfiguration,
} from '@lifeomic/chroma-react/components/TableModule';
import { getTestProps } from '@lifeomic/phc-web-toolkit/src/util/testProps';

const messages = defineMessages({
  date: {
    id: 'auditLog.auditEventTable.date',
    defaultMessage: 'Date',
  },
  action: {
    id: 'auditLog.auditEventTable.action',
    defaultMessage: 'Action',
  },
  resourceType: {
    id: 'auditLog.auditEventTable.resourceType',
    defaultMessage: 'Resource Type',
  },
  agentId: {
    id: 'auditLog.auditEventTable.agent',
    defaultMessage: 'Agent ID',
  },
  rowClickLabel: {
    id: 'auditLog.auditEventTable.rowClickLabel',
    defaultMessage: 'View audit event details',
  },
});

interface AuditEventTableProps {
  events: fhir.AuditEvent[];
  setFocusedEvent: (event: fhir.AuditEvent) => void;
  isLoading: boolean;
  hasMoreToLoad: boolean;
  loadMore: () => Promise<void>;
}

const actions = {
  C: 'Create',
  R: 'Read/View/Print',
  U: 'Update',
  D: 'Delete',
  E: 'Execute',
};

const AuditEventTable: React.FC<AuditEventTableProps> = ({
  events,
  setFocusedEvent,
  isLoading,
  hasMoreToLoad,
  loadMore,
}) => {
  const { formatMessage } = useIntl();

  const config: Array<TableConfiguration> = [
    {
      header: {
        label: formatMessage(messages.date),
      },
      cell: {
        content: (event: fhir.AuditEvent) => {
          const momentVal = moment(event.recorded);
          return momentVal.isValid()
            ? momentVal.format('MMM D, YYYY H:mm')
            : null;
        },
      },
    },
    {
      header: {
        label: formatMessage(messages.action),
      },
      cell: {
        content: (event: fhir.AuditEvent) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          return actions[event.action] || '';
        },
      },
    },
    {
      header: {
        label: formatMessage(messages.resourceType),
      },
      cell: {
        content: (event: fhir.AuditEvent) => {
          return event.entity?.[0]?.reference?.reference?.split('/')[0] || '';
        },
      },
    },
    {
      header: {
        label: formatMessage(messages.agentId),
      },
      cell: {
        content: (event: fhir.AuditEvent) => {
          return event.agent?.[0]?.userId?.value || '';
        },
      },
    },
  ];

  return (
    <InfiniteScroll
      loading={isLoading}
      loadMore={loadMore}
      hasNextPage={hasMoreToLoad}
      scrollContainer="parent"
    >
      <TableModule
        {...getTestProps('audit-event-ui-table')}
        config={config}
        data={events}
        onRowClick={(event: fhir.AuditEvent) => setFocusedEvent(event)}
        isLoading={isLoading}
        rowClickLabel={formatMessage(messages.rowClickLabel)}
      />
    </InfiniteScroll>
  );
};

export default React.memo(AuditEventTable);
